import { useCallback, useRef, useState } from 'react'
import { createElement } from 'react'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import type { ConfirmDialogProps } from '@/shared/components/ConfirmDialog'

/**
 * `confirmDisabled` is here for the refusal-shaped confirm (IN-77): a dialog that explains
 * why the action cannot proceed as asked, leaves the destructive button visibly present but
 * inert, and sends the user back to fix the selection. Cancel still closes it.
 *
 * `children` is the dialog's own slot for a list to read between the sentence and the buttons
 * (CC-26: the recipes an "enable" is about to let loose on clients).
 */
type ConfirmOptions = Pick<ConfirmDialogProps,
  'title' | 'description' | 'confirmLabel' | 'cancelLabel' | 'variant' | 'confirmTestId'
  | 'confirmDisabled' | 'children'>

/**
 * Returns [dialogElement, confirmFn].
 *
 * Usage:
 *   const [confirmDialog, confirm] = useConfirm()
 *
 *   // In a handler:
 *   const ok = await confirm({ title: 'Delete?', variant: 'destructive', confirmLabel: 'Delete' })
 *   if (ok) doDelete()
 *
 *   // In JSX (once, at bottom of component):
 *   {confirmDialog}
 */
export function useConfirm(): [React.ReactNode, (opts: ConfirmOptions) => Promise<boolean>] {
  const [open, setOpen]       = useState(false)
  const [options, setOptions] = useState<ConfirmOptions>({ title: '' })
  // useRef avoids stale-closure issues — always holds the live resolver
  const resolveRef = useRef<((v: boolean) => void) | undefined>(undefined)

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      resolveRef.current = resolve
      setOptions(opts)
      setOpen(true)
    })
  }, [])

  const handleConfirm = useCallback(() => {
    // Clear ref before resolving so the onOpenChange guard below is a no-op
    const resolve = resolveRef.current
    resolveRef.current = undefined
    setOpen(false)
    resolve?.(true)
  }, [])

  const handleCancel = useCallback(() => {
    // Guard: may be called a second time via onOpenChange after handleConfirm
    if (!resolveRef.current) return
    const resolve = resolveRef.current
    resolveRef.current = undefined
    setOpen(false)
    resolve(false)
  }, [])

  const dialog = createElement(ConfirmDialog, {
    open,
    onOpenChange: (v: boolean) => { if (!v) handleCancel() },
    title: options.title,
    description: options.description,
    confirmLabel: options.confirmLabel,
    cancelLabel: options.cancelLabel,
    variant: options.variant,
    confirmTestId: options.confirmTestId,
    confirmDisabled: options.confirmDisabled,
    children: options.children,
    onConfirm: handleConfirm,
  })

  return [dialog, confirm]
}
